import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { getTrackedServiceColumns } from "@/components/trackedService/trackedServiceColumns";
import {
  getMainCategories,
  getTrackedServiceTemplate,
  getTrackedServiceType,
} from "@/components/trackedService/trackedServiceFilterOptions";
import { useActiveKaerUsers } from "@/helper/customHooks/useActiveKaerUsers";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import styles from "@/components/subjectOverview/subjectOverviewAppointments.module.css";
import trackedServiceStyles from "@/components/trackedService/trackedService.module.css";
import { trackedServiceEntityAdapter } from "@/components/trackedService/trackedServiceEntityAdapter";

interface Props {
  appointmentId: string;
}

const AppointmentDashboardTrackedService: FC<Props> = ({ appointmentId }) => {
  const { t } = useTranslation();
  const [mainCategoryOptionsForFiltering, setMaincategoryOptionsForFiltering] =
    useState<any[]>([]);
  const [trackedServiceTypeForFiltering, setTrackedServiceTypeForFiltering] =
    useState<any[]>([]);
  const [
    trackedServiceTemplateForFiltering,
    setTrackedServiceTemplateForFiltering,
  ] = useState<any[]>([]);
  const { userOptions: kaerEmployeeForFiltering } = useActiveKaerUsers({
    variant: "withRelations",
  });

  const statusFilterOptions = getTranslatedStatusOptions("appointment");
  const yesNoFilterOptions = [
    { value: "true", label: t("yes") },
    { value: "false", label: t("no") },
  ];

  useEffect(() => {
    getMainCategories(setMaincategoryOptionsForFiltering);
    getTrackedServiceType(setTrackedServiceTypeForFiltering);
    getTrackedServiceTemplate(setTrackedServiceTemplateForFiltering);
  }, []);

  const columns = useMemo(
    () =>
      getTrackedServiceColumns({
        t,
        styles: trackedServiceStyles,
        includeNavigationLinks: false,
        yesNoFilterOptions,
        statusFilterOptions,
        mainCategoryOptionsForFiltering,
        trackedServiceTypeForFiltering,
        trackedServiceTemplateForFiltering,
        kaerEmployeeForFiltering,
      }),
    [
      t,
      yesNoFilterOptions,
      statusFilterOptions,
      mainCategoryOptionsForFiltering,
      trackedServiceTypeForFiltering,
      trackedServiceTemplateForFiltering,
      kaerEmployeeForFiltering,
    ],
  );

  const customTopToolbarAction = () => {
    return (
      <GenericButton startIcon={<OpenInNewIcon />}>
        <Link
          to="/trackedservice/recorded"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link_white}
        >
          {t("trackedservice_recorded")}
        </Link>
      </GenericButton>
    );
  };

  return (
    <GenericCrudTable
      entity="trackedService"
      entityAdapter={trackedServiceEntityAdapter}
      columns={columns}
      additionalUrlParameter={{
        appointment_id: appointmentId,
        include_test_companies: true,
      }}
      disableAddItem
      showRowActions={false}
      allowColumnFiltering={true}
      disablePersistedColumnFilters
      customDensity="compact"
      customPageSize={5}
      tableCollapseWidth="90%"
      customColumnVisibility={{
        customer_id: false,
        company_subject: false,
        parent_company: false,
        subject: false,
        birth_date: false,
        on_site: false,
        created_on: false,
      }}
      customTopToolBarAction={() => customTopToolbarAction()}
      customGlobalFilterPosition="left"
    />
  );
};

export default AppointmentDashboardTrackedService;
